import React, { useEffect, useState } from 'react';
import { useAuditLogStore } from '@core/auditLog/useAuditLogStore';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Skeleton from './Skeleton';
import ActivityLogItem from './ActivityLogItem';

// Extend dayjs with relative time plugin
dayjs.extend(relativeTime);

interface ActivityProps {
  objectId: string;
  title?: string;
  showLoadingState?: boolean;
  headerActions?: React.ReactNode;
}

const Activity: React.FC<ActivityProps> = ({
  objectId,
  title = 'Activity',
  showLoadingState = true,
  headerActions,
}) => {
  const [hasFetched, setHasFetched] = useState(false);
  const { isLoading, fetchAuditLogsForObject, getAuditLogsForObject } =
    useAuditLogStore();
  const auditLogs = getAuditLogsForObject(objectId);

  useEffect(() => {
    try {
      if (objectId && !isLoading && !hasFetched) {
        fetchAuditLogsForObject(objectId);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setHasFetched(true);
    }
  }, [objectId, fetchAuditLogsForObject, isLoading, hasFetched]);

  if (isLoading && showLoadingState) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="text-xl font-semibold">{title}</h2>
          {headerActions}
        </div>
        <div className="space-y-4">
          <Skeleton height="h-4" width="w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full min-w-0 rounded-lg bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h2 className="text-xl font-semibold">{title}</h2>
        {headerActions}
      </div>
      <div className="min-w-0 space-y-4">
        {auditLogs.length === 0 ? (
          <p className="text-sm text-gray-500">No recent activity</p>
        ) : (
          auditLogs.map((activity) => (
            <ActivityLogItem
              key={activity.id}
              activityId={activity.id}
              notes={activity.notes}
              createdAt={activity.createdAt}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default Activity;
